import {
  ConversationThread,
  InterviewerPersona,
} from '@/components/admin/experiences/conversation-thread';
import { EXPERIENCE_PREVIEW_IDS } from '@/components/admin/experiences/preview/contracts';
import {
  type PreviewStep,
  playsInThread,
  previewBackward,
  previewCrossPage,
  previewEnterTransition,
  previewForward,
  previewPageAt,
  previewPages,
  previewStart,
} from '@/components/admin/experiences/preview/preview-walk';
import {
  PageReader,
  PageTransitionFrame,
  type ReaderFormat,
  type ReaderValue,
  type ReaderValues,
  asStyle,
  readerMotion,
  readerThemeStyle,
  usePrefersReducedMotion,
} from '@/components/reader';
import { useAdminSession } from '@/lib/admin-session';
import { fetchProtocols } from '@/lib/api';
import type { ExperienceDraft } from '@/lib/experience-draft';
import { draftAsContent } from '@/lib/experience-draft';
import {
  type NoyauPreviewPair,
  noyauTextsAt,
  previewNoyau,
  previewNoyauOfCard,
} from '@/lib/noyau-preview';
import { useNoyauTextsSource } from '@/lib/use-noyau-texts';
import { cn } from '@/lib/utils';
import type {
  DisplayConditionSources,
  ExperienceBlockDraft,
  ExperiencePageDraft,
  ExperienceVariable,
  NoyauCode,
  PageTransition,
} from '@matter_extractor/shared';
import {
  isBlockDisplayed,
  noyauBlocksOf,
  noyauOf,
  pageChoiceName,
  pageNoyauChoiceOf,
  resolveTheme,
} from '@matter_extractor/shared';
import { Icon } from '@pms-digital/design-system';
import { useQuery } from '@tanstack/react-query';
import {
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * L'EXPÉRIENCE ENTIÈRE, jouée comme une personne la vit (refonte modulaire
 * §5.3, §9.1 à §9.5 ; issue #141).
 *
 * L'aperçu de l'éditeur tient dans une colonne et ne montre qu'UNE page
 * (`page-editor/page-preview.tsx`), et la scène de la Timeline en montre trois
 * sans en jouer aucune. Ni l'un ni l'autre ne répond à la question qu'un
 * opérateur se pose avant de publier : à quoi ressemble la marche. Ce calque y
 * répond, et il ne répond qu'à ça.
 *
 * ## Un CALQUE, et pas l'API Fullscreen
 *
 * Une session réelle tourne dans un onglet ordinaire, avec la chrome du
 * navigateur autour : le calque est donc la répétition la PLUS fidèle, et le
 * plein écran du navigateur montrerait quelque chose que personne ne verra.
 * Trois conséquences pratiques, et elles ont pesé autant : `Échap` nous
 * appartient, donc un seul appui sort au lieu de rendre d'abord le plein écran ;
 * rien n'a à être demandé à l'utilisateur ni refusé par lui ; et Playwright le
 * traverse sans réserve. C'est enfin l'idiome des sept calques du dépôt.
 *
 * La bande était `z-30` pour l'aperçu et `z-40` pour sa chrome depuis #55. La
 * coquille `AppShell` du design system pose désormais son en-tête collant en
 * `z-40` : le calque et sa chrome passent donc en `z-50`, pour couvrir toujours
 * toute la fenêtre, en-tête et barre de navigation compris.
 *
 * ## Rien ne s'y modifie, et ce que l'on y tape est JETÉ
 *
 * Aucun champ de l'éditeur, aucun réglage. Ce que l'on tape dans les blocs
 * interactifs est une répétition : ça vit dans l'état local ci-dessous, keyé par
 * id de BLOC comme le contrat du lecteur le veut (`components/reader/contracts.ts`),
 * et ça meurt avec le calque. L'écrire dans le brouillon sauverait les réponses
 * d'essai d'un opérateur dans l'expérience que tout le monde joue - c'est
 * l'arbitrage que `page-preview.tsx` fait déjà pour sa colonne, tenu ici pour
 * toute une expérience.
 *
 * ## Un lecteur d'aperçu NEUF, et `SessionPlayer` intact
 *
 * Aucune page n'est peinte deux fois : ce fichier monte les pièces déjà
 * exportées par `components/reader` - `PageReader`, `PageTransitionFrame`,
 * `readerThemeStyle`, `readerMotion` - et `playablePages` de `shared` dit quelles
 * pages se jouent. Seule la MARCHE est écrite deux fois, et c'est délibéré :
 * les deux marches diffèrent vraiment. Celle de la session PERSISTE (le serveur
 * décide de la page suivante, refuse une avancée, reprend une session
 * interrompue) ; celle-ci répète en local, franchit un champ requis, revient en
 * arrière, et jette tout à la sortie. Les fondre aurait mis un « mode aperçu »
 * dans le composant que traverse une personne réelle, ce qu'aucune des deux ne
 * vaut.
 *
 * ## Les variables : la seule surface où la valeur est TAPÉE
 *
 * D4 et D5 interdisent à un aperçu de montrer une valeur - « le nom, jamais une
 * valeur inventée » - parce qu'une valeur inventée se lit comme une donnée.
 * Ici elle n'est pas inventée : elle est tapée, sur une page précédente, par la
 * personne qui regarde. C'est donc la seule exception, et elle est étroite - un
 * champ REMPLI résout ses jetons sur les pages suivantes, et tout le reste
 * (objectifs, critères, choix, champs vides) garde sa PASTILLE à l'accent du
 * thème.
 *
 * Le résolveur ci-dessous est donc PARTIEL, et c'est tout l'écart avec celui de
 * la session : il rend `null` pour ce qu'il ne sait pas, et `reader-text.ts`
 * dessine alors la pastille au lieu d'écrire le jeton. La pastille est ce qui
 * dit « ceci n'est pas du contenu » ; rendu en texte simple, « @prénom » se
 * lirait comme une phrase de la page. Un `{{champ:...}}` brut, lui, n'atteint
 * jamais l'écran (H62) : le chemin d'aperçu ne l'écrit pas.
 *
 * ## Les conditions d'affichage : sur ce que la personne a tapé ou choisi (§9.2, §9.7)
 *
 * The calque evaluates the display conditions with `isBlockDisplayed`, the
 * function a session plays, on what the person LOOKING has done: the fields
 * typed and ticked, and the choice of every page left by a Bouton of a page
 * that retains one - the label pressed, keyed by the page, as the server writes
 * `{{choix:<pageId>}}`. Only the pages BEFORE the one shown count: a condition
 * cites a page before its own (H60), and a box ticked on this page must not
 * unmask a block of this page - the session would not. And a field counts only
 * if it SHOWED, on what the pages before it held, which is how a session writes
 * it (`previewConditions`): walking back and unticking a box masks what that
 * box conditioned, typed text included. No conversation is
 * simulated, so an objective is « vide » (« n'est pas réussi »), and a noyau
 * has no value until the viewer takes a card of the first choice (#151). A
 * masked temps is stepped over by the arrows (`preview-walk.ts`).
 *
 * ## Les blocs Noyau : les vrais textes, pour une paire d'exemple (N20)
 *
 * The cards and the portrait are the REAL texts the experience's reference
 * reads (`useNoyauTextsSource`), for the pair « Aperçu avec » chose in the page
 * editor - the first two noyaux of the table when opened from the Timeline.
 * The card taken at the first choice becomes the retained noyau of the rest of
 * the walk: `@noyau` renders its name, a condition on the noyau reads it, the
 * second choice faces its mirror and the portrait is its own. Until then the
 * blocks play the pair, and `@noyau` keeps its pill.
 */

export interface ExperiencePreviewProps {
  readonly draft: ExperienceDraft;
  /** La page à laquelle on ouvre : celle qu'on édite, ou celle que le rail a sélectionnée. */
  readonly pageId: string | null;
  readonly format: ReaderFormat;
  /** Ce qu'une variable est NOMMÉE sur cet écran (D5) - ce que le résolveur sert par défaut. */
  readonly variableNames: (variable: ExperienceVariable) => string;
  /** « Aperçu avec » (noyau.md N20): the pair the Noyau blocks show until a card is taken. */
  readonly noyauPair: NoyauPreviewPair;
  readonly onClose: () => void;
}

/** Ce que la chrome laisse passer sans geste avant de s'effacer - l'idiome des lecteurs vidéo. */
const IDLE_MS = 2500;

/**
 * Le pas minimum entre deux réveils.
 *
 * Un `pointermove` arrive des dizaines de fois par seconde, et un rendu par
 * évènement ferait ramer la page sous l'expérience la plus simple. Le seuil est
 * très inférieur au repos, donc un geste posé après un endormissement le
 * traverse toujours : le dernier réveil date alors d'au moins `IDLE_MS`.
 */
const WAKE_THROTTLE_MS = 300;

/** La première page n'arrive de nulle part : aucune transition ne la porte (§9.3). */
const NO_TRANSITION: PageTransition = { animation: 'none', speed: 'normal' };

/**
 * Le calque, sous le persona qui nomme la bulle d'une page du fil
 * (`conversation-thread.tsx`).
 */
export function ExperiencePreview(props: ExperiencePreviewProps): ReactNode {
  return (
    <InterviewerPersona personaId={props.draft.persona?.ref.id ?? null}>
      <ExperiencePreviewInner {...props} />
    </InterviewerPersona>
  );
}

function ExperiencePreviewInner({
  draft,
  pageId,
  format,
  variableNames,
  noyauPair,
  onClose,
}: ExperiencePreviewProps): ReactNode {
  const { email } = useAdminSession();
  const pages = useMemo(() => previewPages(draft.pages), [draft.pages]);
  const [step, setStep] = useState<PreviewStep>(() => previewStart(pages, pageId));
  const [values, setValues] = useState<ReaderValues>({});
  /** The Bouton pressed on each page that retains its choice, keyed by page (§8.6). */
  const [choices, setChoices] = useState<Readonly<Record<string, string>>>({});
  /**
   * The noyau of the card TAKEN at the first choice (noyau.md §3.3, §9.7 of the
   * refonte; #151): what `@noyau` renders from there, what a condition on the
   * noyau reads, what the second choice faces and the portrait shows. Null until
   * a card is taken - the Noyau blocks then play the pair of « Aperçu avec » -
   * and null again once `←` walks back to that page or before it.
   */
  const [retained, setRetained] = useState<NoyauCode | null>(null);
  /** Where the walk meets the page of the FIRST choice - or -1, on an experience without one. */
  const firstChoiceAt = useMemo(
    () => pages.findIndex((one) => pageNoyauChoiceOf(one)?.mode === 'first-choice'),
    [pages],
  );

  /* The real texts of the noyaux, at the experience's pin (N20, vitrine.md §3.1). */
  const carriesNoyau = useMemo(() => noyauBlocksOf(draftAsContent(draft)).length > 0, [draft]);
  const noyauPin = draft.noyauTexts?.pin ?? 'latest';
  const noyauSource = useNoyauTextsSource(carriesNoyau, noyauPin);
  // Undefined while the texts are read: the cards say so rather than « Aucun texte encore ».
  const noyauTexts = noyauTextsAt(noyauSource, noyauPin);

  const page = previewPageAt(pages, step);
  const conditions = previewConditions(draft.pages, page, values, choices, retained);
  const displayed = (block: ExperienceBlockDraft): boolean => isBlockDisplayed(block, conditions);
  const prefersReducedMotion = usePrefersReducedMotion();
  const visual = resolveTheme(draft.theme, page?.visual ?? {});
  const motion = readerMotion(visual, prefersReducedMotion);

  /*
    La bibliothèque de §9.5, pour NOMMER le protocole de l'encart et rien de
    plus. `['protocols', email]` est délibérément la clé que la Timeline et
    l'éditeur de page écrivent déjà : même fetcher, même schéma, même forme,
    donc ouvrir l'aperçu depuis la Timeline ne redemande rien. Elle n'est armée
    que si l'expérience porte une page protocole jouable, parce qu'une
    expérience qui n'en a pas n'a personne à nommer.
  */
  const hasProtocolPage = pages.some((one) => one.kind === 'protocol');
  const protocols = useQuery({
    queryKey: ['protocols', email],
    queryFn: fetchProtocols,
    enabled: hasProtocolPage,
    retry: false,
  });

  /* La chrome dort après quelques secondes sans geste, et revient au moindre.
     Le compteur est ce qui relance le minuteur : un horodatage se répéterait
     dans la même milliseconde et l'effet ne repartirait pas. */
  const [awake, setAwake] = useState(true);
  const [wakes, setWakes] = useState(0);
  /** Le focus est DANS la chrome : elle ne s'efface pas sous les doigts d'un clavier. */
  const [held, setHeld] = useState(false);
  const lastWake = useRef(0);
  const wake = useCallback((): void => {
    const now = Date.now();
    if (now - lastWake.current < WAKE_THROTTLE_MS) return;
    lastWake.current = now;
    setAwake(true);
    setWakes((count) => count + 1);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `wakes` is a trigger, not read - it is what restarts the timer at each gesture.
  useEffect(() => {
    if (held) return;
    const timer = window.setTimeout(() => setAwake(false), IDLE_MS);
    return () => window.clearTimeout(timer);
  }, [held, wakes]);

  /*
    Le focus atterrit sur le CALQUE et pas sur la croix, à l'inverse du tiroir
    de §7.1 : la croix s'efface, et laisser le focus sur un contrôle invisible
    serait la pire des deux. La racine porte `tabIndex={-1}`, prend les touches
    par remontée, et `Tab` reste enfermé par `trap` ci-dessous.
  */
  const focusOnMount = useCallback((node: HTMLElement | null) => node?.focus(), []);

  const onKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    wake();
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key === 'Tab') {
      trapTab(event);
      return;
    }
    /* Les flèches appartiennent au CURSEUR dans un champ qu'on est en train de
       remplir : une page qui avancerait sous la frappe rendrait un Input
       inutilisable, et remplir est justement ce que l'aperçu permet. */
    if (editingText(event.target)) return;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      setStep((current) => previewForward(pages, current, displayed));
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      const back = previewBackward(pages, step, displayed);
      setStep(back);
      /* Back on the page of the first choice, or before it: the card taken
         there is undone - the retained noyau exists only from the page that
         FOLLOWS it (§3.3) - and crossing it again takes a card again. */
      if (back.pageIndex <= firstChoiceAt) setRetained(null);
    }
  };

  /* A page of the thread is drawn as the thread - and only when the walk plays it
     there: with the protocol page skipped it is an ordinary page (§9 of the spec). */
  const PageSurface = playsInThread(pages, step.pageIndex) ? ConversationThread : PageReader;
  const transition = previewEnterTransition(pages, step);
  const protocolCard =
    page === null || page.protocol === null
      ? null
      : (protocols.data?.protocols.find((one) => one.id === page.protocol?.id) ?? null);

  return (
    <dialog
      open
      data-testid={EXPERIENCE_PREVIEW_IDS.layer}
      aria-label="Aperçu de l’expérience"
      ref={focusOnMount}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      onPointerMove={wake}
      className="fixed inset-0 z-50 m-0 h-full max-h-none w-full max-w-none border-0 bg-white p-0"
    >
      {/* Le cadre que le thème peint : toute la fenêtre, d'une hauteur définie
          jusqu'à la boîte qui fait défiler la page (issue #168, voir plus bas).
          Le sélecteur d'enfant est ce que `session-player.tsx` fait pour la
          même raison. */}
      <div
        className="h-full w-full overflow-hidden [&>*]:h-full"
        style={asStyle(readerThemeStyle(visual, format))}
      >
        {page === null ? (
          <p className="flex h-full items-center justify-center p-6 text-gray-700 text-small">
            Cette expérience ne porte aucune page jouable.
          </p>
        ) : (
          <PageTransitionFrame
            transition={transition ?? NO_TRANSITION}
            motion={motion}
            pageKey={page.id}
          >
            <div className="relative h-full">
              {/* The page scrolls HERE and not in the reader, whose minimum
                  height bounds nothing - `session-player.tsx` says why (#168).
                  The protocol note stays a sibling, centred on the window. */}
              <div
                data-testid={EXPERIENCE_PREVIEW_IDS.scroll}
                className="h-full overflow-y-auto overflow-x-hidden"
              >
                <PageSurface
                  key={page.id}
                  page={page}
                  theme={draft.theme}
                  format={format}
                  mode="interactive"
                  values={values}
                  revealed={step.revealed}
                  /* Gardé sur la page COURANTE : un minuteur d'une page qu'on
                     vient de quitter n'a pas à rouvrir un temps de celle-ci. Le
                     remontage par `key` le rend improbable, et la garde le rend
                     impossible. */
                  onRevealed={(revealed) =>
                    setStep((current) =>
                      current.pageIndex === step.pageIndex ? { ...current, revealed } : current,
                    )
                  }
                  onValueChange={(blockId: string, value: ReaderValue) =>
                    setValues((current) => ({ ...current, [blockId]: value }))
                  }
                  onAdvance={(advance) => {
                    /* The choice of the page, as the server writes it: a page that
                       retains its choice, left by a Bouton (§8.6). */
                    if (advance.reason === 'button' && pageChoiceName(page) !== null) {
                      const pressed = advance.block.id;
                      setChoices((current) => ({ ...current, [page.id]: pressed }));
                    }
                    /* The card confirmed at the FIRST choice is the retained noyau
                       of the rest of the walk (§3.3); a second choice changes
                       nothing, as in a session. */
                    if (advance.reason === 'noyau' && advance.block.mode === 'first-choice') {
                      setRetained(previewNoyauOfCard(advance.cardId));
                    }
                    setStep((current) => previewCrossPage(pages, current));
                  }}
                  resolveVariable={(variable) => previewResolve(values, variable, retained)}
                  variableNames={variableNames}
                  conditions={conditions}
                  noyau={previewNoyau(page, noyauTexts, noyauPair, retained)}
                />
              </div>
              {page.kind === 'protocol' ? (
                <ProtocolNote
                  name={protocolCard?.name ?? null}
                  reference={page.protocol !== null}
                  read={protocols.data !== undefined}
                />
              ) : null}
            </div>
          </PageTransitionFrame>
        )}
      </div>

      <div
        data-testid={EXPERIENCE_PREVIEW_IDS.chrome}
        data-awake={awake ? 'on' : 'off'}
        onFocusCapture={() => {
          setHeld(true);
          wake();
        }}
        onBlurCapture={() => setHeld(false)}
        className={cn(
          'pointer-events-none absolute inset-x-0 top-0 z-50 flex items-center justify-between gap-3 p-3',
          prefersReducedMotion ? '' : 'transition-opacity duration-300',
          awake ? 'opacity-100' : 'opacity-0',
        )}
      >
        <p
          data-testid={EXPERIENCE_PREVIEW_IDS.position}
          className={cn(
            'm-0 rounded-md bg-white/90 px-3 py-1.5 font-semibold text-bleu-nuit text-caption shadow-sm',
            awake ? 'pointer-events-auto' : 'pointer-events-none',
          )}
        >
          {page === null ? 'Aucune page' : `Page ${step.pageIndex + 1} sur ${pages.length}`}
        </p>
        <button
          type="button"
          data-testid={EXPERIENCE_PREVIEW_IDS.close}
          aria-label="Quitter l’aperçu"
          onClick={onClose}
          className={cn(
            'inline-flex size-9 cursor-pointer items-center justify-center rounded-md bg-white/90 text-bleu-nuit shadow-sm hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit',
            awake ? 'pointer-events-auto' : 'pointer-events-none',
          )}
        >
          <Icon name="close" size={20} />
        </button>
      </div>
    </dialog>
  );
}

/**
 * L'encart de la page protocole (§9.5) - et AUCUNE fausse conversation.
 *
 * La page est affichée et peinte par son visuel, parce qu'elle est une page de
 * l'expérience et qu'on la traverse comme les autres. Ce qui n'est pas joué,
 * c'est l'entretien : ce ne seraient pas les mots de ce protocole, et une
 * maquette de conversation posée ici serait prise pour le produit. C'est la
 * ligne que `protocol-vignette.tsx` tient déjà sur la Timeline - « aucune
 * conversation n'y est simulée » - tenue une seconde fois parce que c'est la
 * seconde surface qui pourrait être tentée.
 *
 * Le nom n'est CLAIMÉ qu'une fois la bibliothèque lue : « Protocole introuvable »
 * affiché pendant la première image de chaque ouverture, sur un protocole
 * parfaitement sain, est le piège de §4 que la scène évite déjà.
 */
function ProtocolNote({
  name,
  reference,
  read,
}: {
  readonly name: string | null;
  /** La page porte-t-elle une référence ? Sinon c'est « sans protocole » et non un manque. */
  readonly reference: boolean;
  readonly read: boolean;
}): ReactNode {
  return (
    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center p-6">
      <div
        data-testid={EXPERIENCE_PREVIEW_IDS.protocolNote}
        className="pointer-events-auto max-w-sm space-y-1 rounded-md border-2 border-gray-200 bg-white/95 p-5 text-center"
      >
        <p className="m-0 font-semibold text-bleu-nuit text-small">Page protocole</p>
        <p className="m-0 text-gray-700 text-small">La conversation ne se joue pas en aperçu.</p>
        <p className="m-0 text-gray-600 text-caption">{protocolLine(name, reference, read)}</p>
      </div>
    </div>
  );
}

function protocolLine(name: string | null, reference: boolean, read: boolean): string {
  if (!reference) return 'Sans protocole.';
  if (!read) return 'Protocole joué : lecture en cours.';
  return name === null ? 'Protocole introuvable.' : `Protocole joué : ${name}.`;
}

/**
 * What the display conditions are evaluated on in the calque (see the head),
 * held the way a SESSION holds it - the mirror of `writtenVariables` of the
 * backend, which writes a page's fields at its advance only if they showed.
 *
 * The pages BEFORE the one shown are walked in timeline order, and each keeps
 * its fields and its choice only if their block SHOWS on what the pages before
 * it have accumulated. Two things a raw record of the typing would get wrong,
 * and a session never does: a value typed on a page AFTER the one shown, the
 * viewer having walked back to it; and a value typed into a field that a later
 * change of mind has masked - a box unticked on page 1 masks the field of page
 * 2, whose typed text then unmasks nothing on page 3. No objective status; and
 * the noyau is the card TAKEN at the first choice of the walk (#151), or none -
 * a condition on it only ever stands after that page, where a session holds it.
 */
function previewConditions(
  pages: readonly ExperiencePageDraft[],
  page: ExperiencePageDraft | null,
  values: ReaderValues,
  choices: Readonly<Record<string, string>>,
  retained: NoyauCode | null,
): DisplayConditionSources {
  const held: Record<string, string | boolean> = {};
  const at = page === null ? -1 : pages.findIndex((one) => one.id === page.id);
  for (const before of pages.slice(0, Math.max(0, at))) {
    // What the session holds ARRIVING on this page: a condition cites a page
    // before its own (H60), so its own fields never decide for each other.
    const reached: DisplayConditionSources = {
      values: { ...held },
      noyau: retained,
      objectives: {},
      pages,
    };
    const blocks = before.temps.flatMap((temps) => temps.blocks);
    for (const block of blocks) {
      if (block.type !== 'input' && block.type !== 'checkbox') continue;
      const value = values[block.id];
      if (value !== undefined && isBlockDisplayed(block, reached)) held[block.id] = value;
    }
    const pressed = blocks.find((block) => block.id === choices[before.id]);
    if (pressed?.type === 'button' && isBlockDisplayed(pressed, reached)) {
      held[before.id] = pressed.label;
    }
  }
  return { values: held, noyau: retained, objectives: {}, pages };
}

/**
 * Ce qu'un jeton rend dans l'aperçu : la valeur TAPÉE, ou rien (voir la tête du
 * fichier).
 *
 * PARTIEL, à l'inverse de `resolvePersonVariable`, qui est total pour la
 * session. « Rien » n'est pas la chaîne vide et n'est pas non plus le jeton
 * brut : c'est `null`, que le chemin d'aperçu de `reader-text.ts` dessine en
 * pastille. Une phrase trouée se lirait comme un défaut de l'expérience alors
 * qu'elle n'est qu'un champ qu'on n'a pas rempli, et le jeton brut est ce qui
 * ne doit jamais atteindre un écran (H62).
 *
 * Seul un CHAMP est résolu. Un objectif et un critère ne vivent que dans une
 * session, et un `{{choix:<pageId>}}` demanderait de rejouer la règle du
 * serveur - deux boutons au moins, « Retenir le choix » coché - dont une copie
 * approximative inventerait justement la valeur que D5 refuse.
 *
 * Une case cochée se lit « oui » / « non », parce qu'une phrase est en train
 * d'être complétée : la lecture que la session fait déjà. Une case qu'on n'a
 * jamais touchée ne porte aucune valeur et garde donc sa pastille.
 *
 * `@noyau` rend le nom déposé du noyau de la carte PRISE au premier choix
 * (noyau.md §3.3, #151) - une valeur, elle aussi, qu'un geste de la personne qui
 * regarde a donnée - et garde sa pastille tant qu'aucune carte n'est prise.
 */
function previewResolve(
  values: ReaderValues,
  variable: ExperienceVariable,
  retained: NoyauCode | null,
): string | null {
  if (variable.kind === 'noyau') return retained === null ? null : noyauOf(retained).name;
  if (variable.kind !== 'field') return null;
  const value = values[variable.id];
  if (typeof value === 'boolean') return value ? 'oui' : 'non';
  if (typeof value === 'string' && value.trim() !== '') return value;
  return null;
}

/** Les éléments qu'un `Tab` peut atteindre - assez pour enfermer le focus, pas un moteur. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Le `focus trap` du calque.
 *
 * Écrit à la main parce que le `<dialog open>` du dépôt est NON modal - c'est le
 * régime que `versioned-dialog.tsx` pose et justifie, React possédant le
 * montage - et que `showModal()` mettrait le calque dans le top layer, où le
 * `z-50` posé au-dessus de la coquille ne voudrait plus rien dire. Le
 * focus qui sort par le bas revient au premier contrôle, celui qui sort par le
 * haut au dernier, et un focus posé hors du calque - la racine elle-même, qui
 * porte `tabIndex={-1}` - repart du premier.
 */
function trapTab(event: KeyboardEvent<HTMLElement>): void {
  const root = event.currentTarget;
  const stops = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
  const first = stops[0];
  const last = stops[stops.length - 1];
  if (first === undefined || last === undefined) {
    event.preventDefault();
    return;
  }
  const active = root.ownerDocument.activeElement;
  if (!(active instanceof HTMLElement) || !stops.includes(active)) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
    return;
  }
  if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus();
    return;
  }
  if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

/**
 * La frappe appartient-elle à un champ qu'on remplit ?
 *
 * Une case à cocher et un bouton radio sont des `input` et ne font rien des
 * flèches : les exclure aurait enfermé la personne sur la page dès qu'elle coche
 * quelque chose, ce qui est exactement le geste que l'aperçu sert à essayer.
 */
function editingText(target: EventTarget | null): boolean {
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLInputElement) {
    return target.type !== 'checkbox' && target.type !== 'radio';
  }
  return target instanceof HTMLElement && target.isContentEditable;
}
